import i18n from 'i18next'

type LocalizedTitles = Record<string, string | undefined>

type LocalizableTitle = {
  title?: string
  localizedTitles?: LocalizedTitles
}

type LocalizableEnumValue = LocalizableTitle & {
  value?: string
}

type LocalizableAttribute = LocalizableTitle & {
  enumValues?: LocalizableEnumValue[]
}

const getLanguageCandidates = () => {
  const language = (i18n.resolvedLanguage || i18n.language || 'en')
    .trim()
    .toLowerCase()

  return language === 'en' ? ['en'] : [language, 'en']
}

export const getLocalizedTitle = <T extends LocalizableTitle>(
  item?: T,
  fallback = '',
) => {
  if (!item) return fallback

  const localizedTitles = item.localizedTitles || {}

  for (const language of getLanguageCandidates()) {
    if (!language) continue

    const localizedTitle = localizedTitles[language]
    if (localizedTitle) return localizedTitle
  }

  return fallback
}

export const getLocalizedSchemaGeneral = <T extends LocalizableTitle>(
  general?: T,
): T | undefined => {
  if (!general) return undefined

  return {
    ...general,
    title: getLocalizedTitle(general),
  }
}

export const getLocalizedSchemaGroups = <
  T extends Record<string, LocalizableTitle>,
>(
  groups?: T,
): T | undefined => {
  if (!groups) return undefined

  return Object.fromEntries(
    Object.entries(groups).map(([key, group]) => [
      key,
      {
        ...group,
        title: getLocalizedTitle(group, key),
      },
    ]),
  ) as T
}

export const getLocalizedSchemaEnumValues = <T extends LocalizableEnumValue>(
  enumValues?: T[],
): T[] | undefined => {
  if (!enumValues) return undefined

  return enumValues.map((enumValue) => ({
    ...enumValue,
    title: getLocalizedTitle(enumValue, enumValue.value || ''),
  })) as T[]
}

export const getLocalizedSchemaAttributes = <
  T extends Record<string, LocalizableAttribute>,
>(
  attributes?: T,
): T | undefined => {
  if (!attributes) return undefined

  return Object.fromEntries(
    Object.entries(attributes).map(([key, attribute]) => [
      key,
      {
        ...attribute,
        title: getLocalizedTitle(attribute, key),
        enumValues: getLocalizedSchemaEnumValues(attribute.enumValues),
      },
    ]),
  ) as T
}
